import React, { Fragment } from 'react';
import { Text, Badge, Group, Title, ActionIcon, Menu } from '@mantine/core';
import {
  IconArchive,
  IconArchiveOff,
  IconDots,
  IconEdit,
  IconGitMerge,
  IconTrash,
} from '@tabler/icons-react';
import { EditableText } from '@ayocore/platform/client/components/EditableText';
import type { PartnerExtended } from '../PartnerViewController';

interface PartnerHeaderProps {
  partner: PartnerExtended;
  canEdit: boolean;
  canDelete: boolean;
  /** Точечное право на архивацию/разархивирование — может отличаться от canEdit по роли. */
  canArchive?: boolean;
  permissionsLoading: boolean;
  onArchive?: () => void;
  onUnarchive?: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onMerge?: () => void;
  onNameSave?: (value: string) => void | Promise<void>;
  onFullNameSave?: (value: string) => void | Promise<void>;
  /** Дополнительные пункты меню от расширений (рендерятся перед «Удалить»). */
  extraMenuItems?: React.ReactNode[];
  /**
   * Дополнительные действия в action bar заголовка (рендерятся слева от Badge).
   * Обычно — кнопки от расширений, которые должны быть видны сразу.
   */
  extraHeaderActions?: React.ReactNode[];
}

export function PartnerHeader({
  partner,
  canEdit,
  canDelete,
  canArchive = false,
  permissionsLoading,
  onArchive,
  onUnarchive,
  onEdit,
  onDelete,
  onMerge,
  onNameSave,
  onFullNameSave,
  extraMenuItems,
  extraHeaderActions,
}: PartnerHeaderProps) {
  const isArchived = !!partner.archived_at;
  const statusColor = partner.customer_status?.color ?? '#868e96';
  const statusLabel = partner.customer_status?.name ?? partner.customer_status?.code ?? '';

  return (
    <div style={{ minWidth: 0, overflow: 'hidden' }}>
      <Group gap='sm' align='center' wrap='nowrap' justify='space-between'>
        <Group gap={6} align='center' wrap='nowrap' style={{ minWidth: 0, maxWidth: '75%' }}>
          {isArchived && (
            <Text span c='dimmed' fw={600} fz={18}>
              [Архивный]
            </Text>
          )}
          {canEdit && onNameSave ? (
            <EditableText
              value={partner.name ?? ''}
              size='lg'
              canEdit
              onSave={v => void onNameSave(v)}
              style={{
                fontWeight: 600,
                minWidth: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            />
          ) : (
            <Title
              order={3}
              style={{
                fontSize: 18,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
            >
              {partner.name}
            </Title>
          )}
        </Group>
        <Group gap='sm' align='center' style={{ flexShrink: 0 }}>
          {extraHeaderActions?.map((node, idx) => <Fragment key={`action-${idx}`}>{node}</Fragment>)}
          <Badge
            color={statusColor}
            variant='light'
            size='sm'
            style={{ textTransform: 'capitalize', fontWeight: 400 }}
          >
            {statusLabel}
          </Badge>
          {(canEdit || canDelete || canArchive || (extraMenuItems && extraMenuItems.length > 0)) && (
            <Menu withArrow position='bottom-end' withinPortal>
              <Menu.Target>
                <ActionIcon
                  variant='light'
                  size='xs'
                  disabled={permissionsLoading}
                  aria-label='Действия'
                >
                  <IconDots size={14} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                {canArchive && onArchive && (
                  <Menu.Item leftSection={<IconArchive size={14} />} onClick={onArchive}>
                    Архивировать
                  </Menu.Item>
                )}
                {canArchive && onUnarchive && (
                  <Menu.Item leftSection={<IconArchiveOff size={14} />} onClick={onUnarchive}>
                    Убрать из архива
                  </Menu.Item>
                )}
                {canEdit && (
                  <Menu.Item leftSection={<IconEdit size={14} />} onClick={onEdit}>
                    Редактировать
                  </Menu.Item>
                )}
                {canEdit && onMerge && (
                  <Menu.Item leftSection={<IconGitMerge size={14} />} onClick={onMerge}>
                    Объединить с дублём
                  </Menu.Item>
                )}
                {extraMenuItems?.map((item, idx) => <Fragment key={idx}>{item}</Fragment>)}
                {canDelete && (
                  <Menu.Item leftSection={<IconTrash size={14} />} color='red' onClick={onDelete}>
                    Удалить
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>
      </Group>
      {canEdit && onFullNameSave ? (
        <EditableText
          value={partner.full_name ?? ''}
          size='sm'
          canEdit
          allowEmptySave
          placeholder='Добавить полное название...'
          onSave={v => void onFullNameSave(v)}
          style={{ color: 'var(--mantine-color-dimmed)', fontWeight: 300, marginTop: 4 }}
        />
      ) : partner.full_name && partner.full_name !== partner.name ? (
        <Text
          c='dimmed'
          size='sm'
          style={{
            fontSize: 14,
            fontWeight: 300,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
            maxWidth: '80%',
            marginTop: 4,
          }}
        >
          {partner.full_name}
        </Text>
      ) : null}
    </div>
  );
}
